import { injectable, inject } from "@launchtray/tsyringe-async";
import { ILatestNewsController } from "./ILatestNewsController";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { ILatestNewsModel } from "../../../common/models/latestnews/ILatestNewsModel";
import { RenterPolicyType } from "../../renterpolicy/components/RenterPolicyType";
import { RenterPolicyPopupScene } from "../../renterpolicy/RenterPolicyPopupScene";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { RenterPolicyFromNewsModel } from "../../../common/models/Renters/RenterPolicyFromNewsModel";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { BuildingRankUpFromNewsModel } from "../../buildingRankUp/model/BuildingRankUpFromNewsModel";
import { BuildingFromNewsModel } from "../../../common/models/Building/BuildingFromNewsModel";
import { ILogger } from "../../../../logger/ILogger";
import { BuildingRightsType } from "by/gamefactory/cityversetycoon/networking/data/responses/user/BuildingRightsType";
import { UpgradeResult } from "../../../common/models/Building/upgrade/UpgradeResult";
import { FixResult } from "../../../common/models/Building/upgrade/FixResult";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { BuildingRankUpPopupSceneFactory, BuildingUpgradeItemPopupSceneFactory } from "../../BuildingSceneFactories";
import { CancellationToken } from "async/CancellationToken";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { IBuildingRankUpModel } from "../../buildingRankUp/model/IBuildingRankUpModel";
import { PlayerModelFromNewsForRenter } from "../models/PlayerModelFromNewsForRenter";

@injectable()
export class LatestNewsController implements ILatestNewsController {
    private readonly contextResolver: IContextResolver;
    private readonly sceneManager: SceneManager;
    private readonly latestNewsModel: ILatestNewsModel;
    private readonly playerModel: IPlayerModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;
    private readonly renterPolicyFactory: (renterPolicyType: RenterPolicyType, renterPolicyFromNewsModel: IRenterPolicyModel) => Promise<RenterPolicyPopupScene>;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        sceneManager: SceneManager,
        @inject("ILatestNewsModel") latestNewsModel: ILatestNewsModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger,
        @inject("RenterPolicyPopupSceneFactory") renterPolicyFactory: (renterPolicyType: RenterPolicyType, renterPolicyFromNewsModel: IRenterPolicyModel) => Promise<RenterPolicyPopupScene>,
    ) {
        this.contextResolver = contextResolver;
        this.sceneManager = sceneManager;
        this.latestNewsModel = latestNewsModel;
        this.playerModel = playerModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.renterPolicyFactory = renterPolicyFactory;
    }

    public async showFixItem(totalRewardInTokens: string, cancellationToken: CancellationToken): Promise<void> {
        const fixResult = new FixResult({
            totalRewardInTokens: totalRewardInTokens,
            currentGuestRewardInTokens: totalRewardInTokens
        });
        const buildingModel = new BuildingFromNewsModel(this.latestNewsModel, fixResult);
        await this.showBuildingUpgradeItem(buildingModel, this.playerModel, cancellationToken);
    }

    public async showUpgradeItem(guestRewardsInTokens: string, cancellationToken: CancellationToken): Promise<void> {
        const upgradeResult = new UpgradeResult({
            totalRewardInTokens: guestRewardsInTokens,
            currentGuestRewardInTokens: guestRewardsInTokens,
            landlordRewardInTokens: this.latestNewsModel.latestNewsInfo.value.landlord?.rewardInTokens ?? "0",
            initialRewardInTokens: this.latestNewsModel.latestNewsInfo.value.initialRewardInTokens,
            piggyMultiplier: this.latestNewsModel.latestNewsInfo.value.piggyMultiplier,
            piggyClubMultiplier: this.latestNewsModel.latestNewsInfo.value.piggyClubMultiplier
        });
        const playerModel = this.latestNewsModel.latestNewsInfo.value.landlord ? new PlayerModelFromNewsForRenter() : this.playerModel;
        const buildingModel = new BuildingFromNewsModel(this.latestNewsModel, upgradeResult);
        await this.showBuildingUpgradeItem(buildingModel, playerModel, cancellationToken);
    }

    public async showRenterPolicy(): Promise<void> {
        const renterPolicyFromNewsModel = new RenterPolicyFromNewsModel(this.latestNewsModel);
        const scene = await this.asyncOperationNotification.autoShowHide(this.renterPolicyFactory(RenterPolicyType.NOTIFICATION_DEFAULT, renterPolicyFromNewsModel));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showRankUp(coinsAmount: number, buildingLevel: number, cancellationToken: CancellationToken): Promise<void> {
        if (this.playerModel.buildingRights.value != BuildingRightsType.LANDLORD) {
            buildingLevel = this.latestNewsModel.latestNewsInfo.value.buildingLevel;
        }
        const rankUpModel = new BuildingRankUpFromNewsModel(this.latestNewsModel, coinsAmount, buildingLevel);
        const buildingModel = new BuildingFromNewsModel(this.latestNewsModel);
        await this.showBuildingRankUpPopup(rankUpModel, buildingModel, cancellationToken);
    }

    public async updateLatestNews(): Promise<void> {
        await this.latestNewsModel.getLatestNews();
        await this.latestNewsModel.getLatestNewsStatus();
        await this.playerModel.loadPlayerData();
    }

    public async updateLatestNewsInfo(id: string): Promise<void> {
        await this.asyncOperationNotification.autoShowHide(this.latestNewsModel.getLatestNewsInfoFromId(id));
    }

    private async showBuildingUpgradeItem(buildingModel: IBuildingModel, playerModel: IPlayerModel, cancellationToken: CancellationToken): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const factory: BuildingUpgradeItemPopupSceneFactory = await this.contextResolver.resolve("BuildingUpgradeItemPopupSceneFactory");
            if (cancellationToken.isCancellationRequested) {
                return;
            }
            const scene = await factory(buildingModel, playerModel, cancellationToken);
            if (cancellationToken.isCancellationRequested) {
                scene.view?.destroy();
                return;
            }
            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing BuildingUpgrade: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async showBuildingRankUpPopup(buildingRankUpModel: IBuildingRankUpModel, buildingModel: IBuildingModel, cancellationToken: CancellationToken): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const factory: BuildingRankUpPopupSceneFactory = await this.contextResolver.resolve("BuildingRankUpPopupSceneFactory");
            if (cancellationToken.isCancellationRequested) {
                return;
            }
            const scene = await factory(buildingRankUpModel, buildingModel, cancellationToken);
            if (cancellationToken.isCancellationRequested) {
                scene.view?.destroy();
                return;
            }
            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing BuildingRankUp: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }
}
